import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  FiArrowRight, FiBox, FiCheckCircle, FiDroplet,
  FiEdit3, FiFeather, FiGlobe, FiGrid, FiLayers, FiMapPin,
  FiPackage, FiSliders, FiTag, FiTruck, FiWind,
} from "react-icons/fi";
import { PACKAGING_TYPES } from "../../data/packaging";
import laundrySheets from "../../Assets/products/klean-leaf-laundry-detergent-sheets-packaging-and-sheets.webp";
import truckLoad from "../../Assets/truck-load.webp";
import ContactModal from "../ContactModal/ContactModal";
import { ProductCategories, FeaturedProducts } from "./HomeProductDiscovery";

const laundryPath = "/products/laundry-detergent-sheets";
const benefits = [
  { icon: FiBox, title: "Custom packaging", text: "Made for your shelf." },
  { icon: FiDroplet, title: "Flexible formulas", text: "Options for your market." },
  { icon: FiLayers, title: "Flexible MOQs", text: "Product-specific order minimums." },
  { icon: FiTag, title: "Private label support", text: "Your brand, thoughtfully developed." },
  { icon: FiMapPin, title: "US sample support", text: "Connect with our Ohio team." },
  { icon: FiGlobe, title: "Global shipping", text: "Production through delivery." },
];
const customization = [
  { icon: FiDroplet, label: "Formula" },
  { icon: FiWind, label: "Fragrance" },
  { icon: FiBox, label: "Packaging" },
  { icon: FiEdit3, label: "Brand design" },
  { icon: FiLayers, label: "Product count" },
  { icon: FiFeather, label: "Materials" },
];
const packaging = [
  { key: "drawerBox", product: "laundry-detergent-sheets" },
  { key: "perforatedBox", product: "laundry-detergent-sheets" },
  { key: "resealablePouch", product: "laundry-pods" },
  { key: "paperEnvelope", product: "laundry-detergent-sheets" },
  { key: "candyWrapper", product: "dishwasher-tablets" },
  { key: "mailerBox", product: "multi-purpose-tablet-kit" },
  { key: "paperCanister", product: "fragrance-booster-beads" },
  { key: "plasticBottle", product: "fragrance-booster-beads" },
];
const process = [
  { icon: FiGrid, label: "Choose a product" },
  { icon: FiSliders, label: "Customize" },
  { icon: FiCheckCircle, label: "Approve samples" },
  { icon: FiPackage, label: "Production" },
  { icon: FiTruck, label: "Ship" },
];

function HomeContent() {
  const [isSampleModalOpen, setIsSampleModalOpen] = useState(false);
  return (
    <>
      <ProductCategories />
      <section className="hp-section hp-partner" aria-labelledby="home-partner-heading">
        <div className="hp-container hp-partner-layout">
          <div className="hp-partner-intro">
            <span className="hp-eyebrow">WHY KLEAN LEAF</span>
            <h2 id="home-partner-heading">Your brand.<br />Our laundry sheet expertise.</h2>
            <div className="hp-moq-highlight">
              <span>Private label laundry sheets from</span>
              <strong>1,000 <span>units*</span></strong>
              <small>*MOQ varies by product, packaging and formula.</small>
              <Link to={laundryPath} className="hp-text-link">See Packaging &amp; MOQ</Link>
            </div>
          </div>
          <div className="hp-benefit-grid">
            {benefits.map(({ icon: Icon, title, text }) => (
              <div className="hp-benefit" key={title}>
                <Icon aria-hidden="true" /><h3>{title}</h3><p>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <FeaturedProducts />
      <section className="hp-section hp-customization" aria-labelledby="home-customization-heading">
        <div className="hp-container hp-customization-layout">
          <Link to={laundryPath} className="hp-customization-visual" aria-label="Explore laundry detergent sheets and custom packaging">
            <img src={laundrySheets} alt="Klean Leaf laundry detergent sheet packaging with loose sheets and botanical artwork" width={1600} height={1200} loading="lazy" decoding="async" />
            <span className="hp-image-caption">Every detail, considered.</span>
          </Link>
          <div className="hp-customization-copy">
            <span className="hp-eyebrow">MAKE IT YOURS</span>
            <h2 id="home-customization-heading">Custom laundry sheets.<br />Made for your brand.</h2>
            <div className="hp-customization-grid">
              {customization.map(({ icon: Icon, label }) => <div key={label}><Icon aria-hidden="true" /><span>{label}</span></div>)}
            </div>
            <p className="hp-note">Customization options vary by product.</p>
            <Link to={laundryPath} className="hp-text-link">Explore Laundry Sheet Customization</Link>
          </div>
        </div>
      </section>
      <section className="hp-section hp-packaging" aria-labelledby="home-packaging-heading">
        <div className="hp-container">
          <div className="hp-section-heading">
            <div><span className="hp-eyebrow">THE FINISHING TOUCH</span><h2 id="home-packaging-heading">Packaging built around your brand.</h2></div>
            <Link to={laundryPath} className="hp-text-link">See Laundry Sheet Packaging</Link>
          </div>
          <div className="hp-packaging-grid">
            {packaging.map(({ key, product }) => {
              const format = PACKAGING_TYPES[key];
              return (
                <Link key={key} to={`/products/${product}`} className="hp-packaging-item" aria-label={`Explore ${format.label.toLowerCase()} packaging options`}>
                  <img src={format.image} alt={`${format.label} packaging illustration`} width={format.width} height={format.height} loading="lazy" decoding="async" />
                  <span>{format.label}</span>
                </Link>
              );
            })}
          </div>
          <p className="hp-note">Available packaging varies by product and order quantity.</p>
        </div>
      </section>
      <section className="hp-section hp-process" aria-labelledby="home-process-heading">
        <div className="hp-container">
          <div className="hp-section-heading">
            <div><span className="hp-eyebrow">FROM FIRST IDEA TO FIRST ORDER</span><h2 id="home-process-heading">A clear path to your product line.</h2></div>
            <Link to={laundryPath} className="hp-text-link">Explore Laundry Detergent Sheets</Link>
          </div>
          <ol className="hp-process-steps">
            {process.map(({ icon: Icon, label }, index) => (
              <li key={label}>
                <span className="hp-step-top"><span className="hp-step-number">0{index + 1}</span><Icon aria-hidden="true" /></span>
                <h3>{label}</h3>
                {index < process.length - 1 && <FiArrowRight className="hp-step-arrow" aria-hidden="true" />}
              </li>
            ))}
          </ol>
        </div>
      </section>
      <section className="hp-section hp-samples" aria-labelledby="home-sample-heading">
        <div className="hp-container hp-sample-layout">
          <div className="hp-sample-illustration" aria-hidden="true"><FiPackage /><span>FROM OUR OHIO TEAM</span></div>
          <div><span className="hp-eyebrow">TRY IT FIRST</span><h2 id="home-sample-heading">A little sample.<br />A clearer next step.</h2><p>Compare laundry sheet formulas, scents and packaging before you launch.</p></div>
          <button className="hp-button" type="button" onClick={() => setIsSampleModalOpen(true)}>Request Samples <FiArrowRight aria-hidden="true" /></button>
        </div>
      </section>
      <section className="hp-section hp-compact" aria-labelledby="home-compact-heading">
        <div className="hp-container hp-compact-layout">
          <div className="hp-compact-copy">
            <span className="hp-eyebrow">COMPACT BY DESIGN</span>
            <h2 id="home-compact-heading">Less bulk.<br />More room to grow.</h2>
            <p>Compact laundry sheets make storage and shipping simpler, from your first order to what comes next.</p>
          </div>
          <img
            className="hp-compact-image"
            src={truckLoad}
            alt="Klean Leaf van carrying compact laundry sheet cartons beside a large truck filled with liquid detergent bottles"
            width={1448}
            height={1086}
            loading="lazy"
            decoding="async"
          />
        </div>
      </section>
      <section className="hp-final-cta" aria-labelledby="home-final-heading">
        <div className="hp-container">
          <span className="hp-eyebrow">LET’S BUILD SOMETHING THAT’S YOURS</span>
          <h2 id="home-final-heading">Ready to launch your laundry sheet brand?</h2>
          <p>Start with private label laundry detergent sheets. Grow into a complete cleaning product line.</p>
          <div className="hp-actions"><Link className="hp-button" to="/products">Explore Products <FiArrowRight aria-hidden="true" /></Link><Link to="/contact" className="hp-text-link">Contact Us</Link></div>
        </div>
      </section>
      <ContactModal title="Request Samples" mobileTypography isModalOpen={isSampleModalOpen} handleCancel={() => setIsSampleModalOpen(false)} idPrefix="home-sample-contact" />
    </>
  );
}
export default HomeContent;
